import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { request } from '../../api/client';
import { Card } from '../../ui/components';
import { formatBytes, formatNumber } from '../../ui/format';

const PERIODS = ['today', 'last_7_days', 'this_month', 'last_month'] as const;

/**
 * SIM traffic of the device's own connections (docs/architecture/14): the gateway's wire estimate (INFERRED: payload +
 * IP/TCP headers, ≈) and the measured TCP payload per period. A device behind a concentrator has no SIM: its traffic
 * travels on the concentrator's SIM, which is linked instead.
 */
export function TrafficCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['device', id, 'traffic'],
    queryFn: () =>
      request('GET', `/api/v1/devices/${id}/traffic?days=31`, undefined, (v) => api.DeviceTraffic.parse(v)),
    refetchInterval: 15 * 60_000,
  });
  const d = q.data;
  return (
    <Card title={t('devices.traffic.title')}>
      {q.isLoading ? (
        <p className="muted small">{t('common.loading')}</p>
      ) : !d ? (
        <p className="muted small">{t('devices.traffic.unavailable')}</p>
      ) : d.carried_by ? (
        <p className="muted small" style={{ margin: 0 }}>
          {t('devices.traffic.carriedBy')}{' '}
          <Link to={`/devices/${d.carried_by.id}`}>{d.carried_by.name ?? d.carried_by.id}</Link>
        </p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('devices.traffic.period')}</th>
                  <th className="num">{t('devices.traffic.estimated')}</th>
                  <th className="num">{t('devices.traffic.payload')}</th>
                  <th className="num">{t('devices.traffic.connections')}</th>
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((p) => {
                  const v = d.totals[p];
                  return (
                    <tr key={p}>
                      <td>{t(`devices.traffic.periods.${p}`)}</td>
                      <td className="num">≈ {formatBytes(v.estimated_wire_bytes)}</td>
                      <td className="num" title={t('devices.traffic.rxTx')}>
                        {formatBytes(v.rx_bytes)} / {formatBytes(v.tx_bytes)}
                      </td>
                      <td className="num">{formatNumber(v.connections)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted small" style={{ marginBottom: 0 }}>
            {t('devices.traffic.hint')}
          </p>
        </>
      )}
    </Card>
  );
}
